import { type Except, notEmpty, ReactUtils } from '@festina-life/core'
import {
  Field as HeadlessField,
  Label as HeadlessLabel,
} from '@headlessui/react'
import cx from 'classnames'
import {
  type ComponentProps,
  type CSSProperties,
  type ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import { Alert } from '../../feedback/alert/Alert'
import { Tooltip } from '../../feedback/tooltip/Tooltip'
import { useTranslate } from '../../hooks/useTranslate'
import { paperStyles } from '../../libs/paperStyles'
import { zIndexStyles } from '../../libs/zIndexStyles'
import { Td } from '../../listsAndTables/table/Table'
import { Icon } from '../../structure/icon/Icon'
import { typographyStyles } from '../../structure/typography/styles'
import { inputWrapperStyles } from './inputWrapperStyles'

const defaultInputAlignment = {
  top: 0,
  left: 0,
  width: 0,
  height: 0,
}

export type InputProps = {
  wrapperProps: Props
}

export type InputWrapperPropsBase = {
  htmlFor?: ComponentProps<'label'>['htmlFor']
  description?: string | ReactNode
  validInput?: string | ReactNode
  defaultValue?: string | ReactNode
  onDefaultValueClick?: () => void
  onExistingValueClick?: () => void
  validationText?: string
  error?: string
  isRequired?: boolean
  /**
   * For some headless ui components we need to use the headless ui labels for proper accessibility/testing.
   * https://headlessui.com/react/checkbox#adding-a-label
   */
  shouldUseHeadlessLabels?: boolean
  hasRightColumnAlways?: boolean
  hasCustomWidth?: boolean
  existingValueConfig?: {
    existingValue?: string | ReactNode
    inputValueDifferenceFromExistingValue?:
      | 'identical'
      | 'bigger'
      | 'smaller'
      | 'different'
  }
} & (
  | {
      label: ReactNode
      isTableCell?: false
    }
  | {
      /**
       * When the input is used in a table cell, we need to use a different layout to ensure the input fits in the cell.
       * We limit what is shown by default and render the extra stuff in a popover behind the input.
       */
      isTableCell: true
      label?: never
    }
)

type Props = InputWrapperPropsBase &
  Except<ComponentProps<'div'>, 'defaultValue'>

/**
 * InputWrapper component used to display labels, descriptions and error messages around form inputs in
 * a uniform manner.
 *
 * With existingValueConfig prop it is possible to control if the existingValue and the input component are renderd.
 * By default if the existingValues object from the ExistingValuesContext is present, the input components
 * are not rendered. By clicking the existingValue the input component will be rendered.
 *
 * ## Data points to show
 *
 * Prioritized by importance in ascending order
 *
 * - Label
 * - Error
 * - Is required
 * - Default value
 * - Validation text
 * - Previous value
 * - Description
 *
 * ## Underlying needs
 *
 * ### Scanability
 *
 * We want to make sure that the user can quickly scan the form and quickly find a specific field, and get a overview of the values of that field, and the related meta data.
 * Because of this we have gone for a very coherent layout, where the label, input and metadata is very close together to avoid the eyes of the user to bounce back and forth between labels, inputs and meta information.
 *
 * ### Limited vertical space
 *
 * The ui should be optimized to work on desktop monitors or laptops. These screens are wider than they are tall, and form inputs are always stacked vertically.
 * This means that we have a lot of horizontal space, but limited vertical space, and we should try to fit as many fields as possible on the screen at the same time.
 *
 * Despite of this we have chosen to put the labels on top of the inputs, since all our alternative layouts with the label to the left of the input have a worse UX, and the added meta data resultet in the same amount of vertical space used anyway.
 *
 * ### Pin a value
 *
 * It should be possible to easily copy over the default value to be the actual value of the input. This is done by clicking on the default value.
 *
 * ### Cleaning and 'none' value
 *
 * It should be possible to easily remove the value of the input, and set it to 'none'. This will be the responsibility of the input components itself, not this wrapper.
 *
 * ### Give understanding of what is submitted
 *
 * The user should easily be able to identify what the resulting value of the field will be, when submitting the form. This is done by showing the default value as the placeholder of the input.
 * The deveoper should also provide all relevant information that is automatically added to the form data, so the user can easily see what is submitted.
 *
 * ### Consistent input types
 *
 * All inputs should have the same look and feel, and provide the same information. This is done by having the same layout for all inputs, and the same meta data.
 * Only providing the meta data as a placeholder for the input, will not work for all inputs, like checkboxes, radio buttons and the like.
 *
 * ### Localized inputs
 *
 * All information should be shown in a localized manner. This means that dates should be shown in the correct format, and amounts should be shown in the correct currency.
 *
 * ### No disappearing information
 *
 * All available meta information must be availabe at all times. This means that the default value, validation text and previous must not disappear when a value is entered.
 */
export const InputWrapper = ({
  htmlFor,
  label,
  description,
  defaultValue,
  onDefaultValueClick,
  onExistingValueClick,
  validationText,
  error,
  isRequired,
  children: input,
  className,
  hasRightColumnAlways = false,
  existingValueConfig,
  shouldUseHeadlessLabels = false,
  isTableCell = false,
  hasCustomWidth = false,
  ...restProps
}: Props) => {
  const translate = useTranslate()
  const [hasTableCellFocus, setHasTableCellFocus] = useState(false)
  const tableCellInputRef = useRef<HTMLDivElement>(null)
  const popupInputRef = useRef<HTMLDivElement>(null)
  const [inputAlignment, setInputAlignment] = useState(defaultInputAlignment)
  const { existingValue, inputValueDifferenceFromExistingValue } =
    existingValueConfig ?? {}
  const hasRightColumn =
    Boolean(defaultValue || validationText || existingValue) ||
    hasRightColumnAlways

  const hasAnyContentBesidesInput = Boolean(
    hasRightColumn || label || description || error,
  )

  const Label = shouldUseHeadlessLabels ? HeadlessLabel : 'label'
  const Wrapper = shouldUseHeadlessLabels ? HeadlessField : 'div'
  const hasDifference =
    inputValueDifferenceFromExistingValue &&
    inputValueDifferenceFromExistingValue !== 'identical'
  const hasElementsAboveChildren = Boolean(
    label || description || hasDifference,
  )

  useLayoutEffect(
    // Note that we deliberately don't use floating UI or similar since we need this popover to be consistently placed related to the tablecell. The containers around the table normally allow overflow scroll so if we e.g. stick out to the right this shouldn't be a problem.
    function HandleInputPopoverAlignment() {
      if (!hasTableCellFocus || !hasAnyContentBesidesInput || !isTableCell) {
        return
      }

      const tableCellInput = tableCellInputRef.current
      if (!tableCellInput) {
        return
      }

      const updateInputPopoverAlignment = () => {
        const tableCellInputSizes = tableCellInput.getBoundingClientRect()
        const popupInput = popupInputRef.current
        if (!popupInput) {
          return
        }

        popupInput.style.setProperty(
          'height',
          `${tableCellInputSizes.height}px`,
        )
        popupInput.style.setProperty('width', `${tableCellInputSizes.width}px`)
        const popupInputSizes = popupInput.getBoundingClientRect()
        const tableCell = tableCellInput.closest<HTMLElement>(
          '[data-amaze-tablecell]',
        )
        const currentTopAdjustment =
          Number.parseFloat(
            tableCell?.style.getPropertyValue('--top-adjustment') ?? '',
          ) || 0
        const currentLeftAdjustment =
          Number.parseFloat(
            tableCell?.style.getPropertyValue('--left-adjustment') ?? '',
          ) || 0

        setInputAlignment({
          top:
            tableCellInputSizes.top -
            popupInputSizes.top +
            currentTopAdjustment,
          left:
            tableCellInputSizes.left -
            popupInputSizes.left +
            currentLeftAdjustment,
          width: tableCellInputSizes.width,
          height: tableCellInputSizes.height,
        })
      }

      updateInputPopoverAlignment()

      if (typeof ResizeObserver === 'undefined') {
        return
      }

      const resizeObserver = new ResizeObserver(updateInputPopoverAlignment)
      resizeObserver.observe(tableCellInput)

      return () => resizeObserver.disconnect()
    },
    [hasAnyContentBesidesInput, hasTableCellFocus, isTableCell],
  )

  const clearTableCellFocus = () => {
    setHasTableCellFocus(false)
    setInputAlignment(defaultInputAlignment)
  }

  const handleFocus = () => {
    if (hasAnyContentBesidesInput && isTableCell) {
      setHasTableCellFocus(true)
    }
  }

  return (
    <ReactUtils.ConditionalWrap
      condition={isTableCell}
      wrap={(inputWrapper) => (
        <Td
          onFocus={() => {
            handleFocus()
          }}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) {
              clearTableCellFocus()
            }
          }}
          onKeyUp={(event) => {
            if (event.key === 'Escape') {
              clearTableCellFocus()
            }
          }}
          data-amaze-tablecell
          className={cx(
            'relative p-1! bg-slate-100 dark:bg-slate-800',
            'border-slate-200! dark:border-slate-700! border-l border-r',
          )}
          style={
            {
              '--left-adjustment': inputAlignment
                ? `${inputAlignment.left}px`
                : undefined,
              '--top-adjustment': inputAlignment
                ? `${inputAlignment.top}px`
                : undefined,
            } as CSSProperties
          }
        >
          <div
            className={cx(
              'relative',
              '**:min-w-0!',
              hasTableCellFocus && zIndexStyles('popovers'),
            )}
            ref={tableCellInputRef}
          >
            {input}
            {isRequired && (!hasTableCellFocus || !label) && (
              <span
                className={cx(
                  'absolute -top-1 -left-1 z-1 px-0.5 rounded-br-lg text-error',
                  paperStyles({
                    borders: ['right', 'bottom'],
                  }),
                  'leading-none',
                  typographyStyles({ weight: 'semibold' }),
                )}
              >
                *
              </span>
            )}
          </div>
          <div
            className={cx(
              zIndexStyles('popoverBackdrop'),
              hasTableCellFocus ? 'absolute' : 'hidden',
              'translate-x-(--left-adjustment) translate-y-(--top-adjustment)',
            )}
          >
            {inputWrapper}
          </div>
        </Td>
      )}
    >
      <Wrapper
        className={cx(
          inputWrapperStyles({ hasRightColumn, isTableCell, hasCustomWidth }),
          isTableCell &&
            cx(
              paperStyles({ borders: ['all'] }),
              'pt-[calc(var(--spacing)-1px)] -mt-[calc(var(--spacing)-1px)]', // To align with borders inside the table
              'p-1 -m-1',
              'shadow-sm',
            ),

          className,
        )}
        {...restProps}
        data-amaze-inputwrapper
      >
        {/* left column start */}
        <div>
          {hasElementsAboveChildren && ( // check needed to ensure we don't have an empty div with padding-bottom when there is no label, description or difference icon
            <div
              className={cx(
                'flex items-end',
                'pb-0.5', // Padding here and not above to ensure there is no gap between the label and the input
              )}
            >
              {inputValueDifferenceFromExistingValue &&
                inputValueDifferenceFromExistingValue !== 'identical' && (
                  <Tooltip
                    tip={
                      {
                        bigger: translate('inputWrapper.bigger'),
                        smaller: translate('inputWrapper.smaller'),
                        different: translate('inputWrapper.different'),
                      }[inputValueDifferenceFromExistingValue]
                    }
                    className="me-1"
                  >
                    <Icon
                      color="unset"
                      className={cx(error ? 'text-error' : 'text-info')}
                      name={
                        {
                          bigger: 'ArrowUpRight' as const,
                          smaller: 'ArrowDownRight' as const,
                          different: 'Sparkle' as const,
                        }[inputValueDifferenceFromExistingValue]
                      }
                    />
                  </Tooltip>
                )}
              {label && (
                <Label
                  className={cx(
                    typographyStyles({ weight: 'semibold' }),
                    'flex',
                  )}
                  htmlFor={htmlFor}
                >
                  {label}
                  {isRequired ? (
                    <span className="-translate-y-0.5 text-error">*</span>
                  ) : null}
                </Label>
              )}
              {description && (
                <Alert severity="info" variant="mini" className="ms-auto">
                  {description}
                </Alert>
              )}
            </div>
          )}
          <div ref={popupInputRef}>{!isTableCell && input}</div>
        </div>
        {/* left column end - right column start */}
        {hasRightColumn && (
          <div
            className={cx(
              'flex flex-col -mb-px',
              '*:not-first:-mt-0.5',
              typographyStyles({ intensity: 'secondary', size: 'sm' }),
            )}
          >
            {notEmpty(existingValue) &&
              existingValue !== '-' && ( // Boolean translations of empty values are translated to '-'
                // We bottom align inside the right column so the first items need no fallback for layout consistency
                <Tooltip tip={translate('inputWrapper.existingValueTooltip')}>
                  <div
                    role="button"
                    data-testid="existingValue"
                    onMouseDown={(event) => {
                      // Keep focus on the input so clicking this action does not
                      // trigger an extra blur callback before the click handler.
                      event.preventDefault()
                    }}
                    onClick={existingValue ? onExistingValueClick : undefined}
                    className="truncate cursor-pointer"
                  >
                    {existingValue}
                  </div>
                </Tooltip>
              )}
            {notEmpty(defaultValue) ? (
              <Tooltip tip={translate('inputWrapper.defaultValueTooltip')}>
                <div
                  role="button"
                  data-testid="defaultValue"
                  onMouseDown={(event) => {
                    // Keep focus on the input so clicking this action does not
                    // trigger an extra blur callback before the click handler.
                    event.preventDefault()
                  }}
                  onClick={defaultValue ? onDefaultValueClick : undefined}
                  className="truncate cursor-pointer"
                >
                  {defaultValue}
                </div>
              </Tooltip>
            ) : (
              <DummyForConsistentLayout />
            )}
            {validationText ? (
              <div className="truncate">{validationText}</div>
            ) : (
              <DummyForConsistentLayout />
            )}
          </div>
        )}
        {/* right column end */}
        {error && (
          <div className="-mb-0.5 text-error text-sm col-span-full flex items-center pt-0.5">
            {error}
          </div>
        )}
      </Wrapper>
    </ReactUtils.ConditionalWrap>
  )
}

const DummyForConsistentLayout = () => (
  <div>{'\xa0' /* nbsp for layout consistency */}</div>
)
